// src/models/APIModel.ts

declare global {
  interface Window {
    __ENV__?: {
      VITE_API_URL?: string;
      [key: string]: any;
    };
  }
  // Do not redeclare ImportMeta.env; use the type provided by Vite.
}

export interface APIEndpoints {
  auth: {
    login: string;
    register: string;
    me: string;
    update: string;
    refresh: string;
    logout: string;
    users: {
      list: string;
      update: (userId: string) => string;
    };
  };
  sites: {
    list: string;
    get: (id: string) => string;
    create: string;
    update: (id: string) => string;
    delete: (id: string) => string;
  };
  fleet: {
    overview: (product?: string) => string;
  };
  devices: {
    list: string;
    get: (id: string) => string;
    getSites: (siteId: string) => string;
    create: string;
    update: (id: string) => string;
    delete: (id: string) => string;
    heartbeat: (deviceId: string) => string;
    quality: (deviceId: string) => string;
    writeCommand: string;
    writeCommandResult: string;
    writeAudit: string;
    pendingWriteCommands: string;
  };
  registers: {
    listBySite: (siteId: string) => string;
    get: (registerId: string) => string;
    advanced: (siteId: string) => string;
    create: string;
    refresh: (siteId: string) => string;
    history: (siteId: string, registerId: string) => string;
    update: (id: string) => string;
    delete: (id: string) => string;
    assign: (siteId: string) => string;
    validationRules: (id: string) => string;
  };
  dictionary: {
    list: string;
    create: string;
    update: (id: string) => string;
    delete: (id: string) => string;
    bulk: string;
  };
  readings: {
    registerReadings: string;
    registerReadingById: (readingId: string) => string;
    registerHistory: (siteId: string, registerId: string) => string;
    deviceReadings: string;
    deviceReadingById: (readingId: string) => string;
  };
  alarm: {
    post: string;
    list: string;
    count: string;
    acknowledge: (alarmId: string) => string;
    unacknowledge: (alarmId: string) => string;
    bulkAcknowledge: string;
    clear: (alarmId: string) => string;
    statistics: string;
    export: string;
    shelve: (id: string) => string;
    unshelve: (id: string) => string;
  };
  kpi: {
    current: (siteId: string) => string;
    history: (siteId: string) => string;
    liveHistory: (siteId: string) => string;
    summary: (siteId: string) => string;
    energyMix: (siteId: string) => string;
    peakDemand: (siteId: string) => string;
  };
  assets: {
    pv: (siteId: string) => string;
    pvSummary: (siteId: string) => string;
    pvHistory: (siteId: string) => string;
    battery: (siteId: string) => string;
    batterySummary: (siteId: string) => string;
    batteryHistory: (siteId: string) => string;
    generator: (siteId: string) => string;
    generatorSummary: (siteId: string) => string;
    generatorHistory: (siteId: string) => string;
    grid: (siteId: string) => string;
    gridSummary: (siteId: string) => string;
    gridHistory: (siteId: string) => string;
  };
  weather: {
    current: (siteId: string) => string;
    solarPosition: (siteId: string) => string;
    forecast: (siteId: string) => string;
  };
  topology: {
    get: (siteId: string) => string;
    update: (siteId: string) => string;
    live: (siteId: string) => string;
  };
  sld: {
    runtime: (siteId: string) => string;
    runtimeLive: (siteId: string) => string;
  };
  eventLogs: {
    list: string;
    create: string;
    export: string;
    soe: (siteId: string) => string;
  };
  ems: {
    config: (siteId: string) => string;
    status: (siteId: string) => string;
    setMode: (siteId: string) => string;
    setParameters: (siteId: string) => string;
    history: (siteId: string) => string;
  };
  controlAuthority: {
    get: (deviceId: string) => string;
    set: (deviceId: string) => string;
    listBySite: (siteId: string) => string;
    summaryBySite: (siteId: string) => string;
  };
  diagnostics: {
    get: (siteId: string) => string;
  };
  security: {
    events: string;
    summary: string;
  };
  audit: {
    list: string;
    export: string;
  };
  registerStandard: {
    json: string;
    html: string;
  };
  document: {
    get: (siteId: string) => string;
    delete: (id: string) => string;
    upload: string;
  };
  carbon: {
    summary: (siteId: string) => string;
  };
}

export class APIModel {
  baseUrl: string;
  endpoints: APIEndpoints;

  constructor() {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (isLocalhost) {
      // In local development, force same-origin calls so Vite proxy (/v1) handles cross-origin.
      this.baseUrl = '/v1';
    } else {
    // Check for runtime config first (from config.js injected by Docker)
    // Then fall back to build-time env var, then default
      this.baseUrl = ((window.__ENV__?.VITE_API_URL)
        || import.meta.env.VITE_API_URL
        || "https://api.lapomikenergy.com").replace(/\/+$/, '');
    }

    const apiBase = /\/v\d+$/i.test(this.baseUrl)
      ? this.baseUrl
      : `${this.baseUrl}/v1`;

    this.endpoints = {
      auth: {
        login: `${apiBase}/auth/login`,
        register: `${apiBase}/auth/register`,
        me: `${apiBase}/auth/me`,
        // Legacy/self-profile fallback route (kept for compatibility)
        update: `${apiBase}/auth/update`,
        refresh: `${apiBase}/auth/refresh`,
        logout: `${apiBase}/auth/logout`,
        users: {
          list: `${apiBase}/users`,
          update: (userId: string) => `${apiBase}/users/${userId}`,
        },
      },

      sites: {
        list: `${apiBase}/sites`,
        get: (id: string) => `${apiBase}/sites/${id}`,
        create: `${apiBase}/sites`,
        update: (id: string) => `${apiBase}/sites/${id}`,
        delete: (id: string) => `${apiBase}/sites/${id}`,
      },

      fleet: {
        overview: (product?: string) =>
          product
            ? `${apiBase}/fleet?product=${encodeURIComponent(product)}`
            : `${apiBase}/fleet`,
      },

      devices: {
        list: `${apiBase}/devices`,
        get: (id: string) => `${apiBase}/devices/${id}`,
        getSites: (siteId: string) => `${apiBase}/devices/site/${siteId}`,
        create: `${apiBase}/devices`,
        update: (id: string) => `${apiBase}/devices/${id}`,
        delete: (id: string) => `${apiBase}/devices/${id}`,
        heartbeat: (deviceId: string) => `${apiBase}/devices/${deviceId}/heartbeat`,
        quality: (deviceId: string) => `${apiBase}/devices/${deviceId}/quality`,
        writeCommand: `${apiBase}/devices/writeCommand`,
        writeCommandResult: `${apiBase}/devices/writeCommandResult`,
        writeAudit: `${apiBase}/devices/writeAudit`,
        pendingWriteCommands: `${apiBase}/devices/write-commands/pending`,
      },

      registers: {
        listBySite: (siteId: string) => `${apiBase}/registers/site/${siteId}`,
        get: (registerId: string) => `${apiBase}/registers/${registerId}`,
        advanced: (siteId: string) => `${apiBase}/registers/site/${siteId}/advanced`,
        create: `${apiBase}/registers`,
        refresh: (siteId: string) => `${apiBase}/registers/refresh/${siteId}`,
        history: (siteId: string, registerId: string) =>
          `${apiBase}/registers/${siteId}/history/${registerId}`,
        update: (id: string) => `${apiBase}/registers/${id}`,
        delete: (id: string) => `${apiBase}/registers/${id}`,
        assign: (siteId: string) => `${apiBase}/registers/${siteId}/assign`,
        validationRules: (id: string) => `${apiBase}/registers/${id}/validation-rules`,
      },

      dictionary: {
        list: `${apiBase}/registers/dictionary`,
        create: `${apiBase}/registers/dictionary`,
        update: (id: string) => `${apiBase}/registers/dictionary/${id}`,
        delete: (id: string) => `${apiBase}/registers/dictionary/${id}`,
        bulk: `${apiBase}/registers/dictionary/bulk`,
      },

      readings: {
        registerReadings: `${apiBase}/readings/register-readings`,
        registerReadingById: (readingId: string) => `${apiBase}/readings/register-readings/${readingId}`,
        registerHistory: (siteId: string, registerId: string) =>
          `${apiBase}/readings/register-readings/${siteId}/history/${registerId}`,
        deviceReadings: `${apiBase}/readings`,
        deviceReadingById: (readingId: string) => `${apiBase}/readings/${readingId}`,
      },

      alarm: {
        post: `${apiBase}/alarms`,
        list: `${apiBase}/alarms`,
        count: `${apiBase}/alarms/count`,
        acknowledge: (alarmId: string) => `${apiBase}/alarms/${alarmId}/acknowledge`,
        unacknowledge: (alarmId: string) => `${apiBase}/alarms/${alarmId}/unacknowledge`,
        bulkAcknowledge: `${apiBase}/alarms/bulk-acknowledge`,
        clear: (alarmId: string) => `${apiBase}/alarms/${alarmId}/clear`,
        statistics: `${apiBase}/alarms/statistics`,
        export: `${apiBase}/alarms/export`,
        shelve: (id: string) => `${apiBase}/alarms/${id}/shelve`,
        unshelve: (id: string) => `${apiBase}/alarms/${id}/unshelve`,
      },

      kpi: {
        current: (siteId: string) => `${apiBase}/kpi/${siteId}/current`,
        history: (siteId: string) => `${apiBase}/kpi/${siteId}/history`,
        liveHistory: (siteId: string) => `${apiBase}/kpi/${siteId}/live-history`,
        summary: (siteId: string) => `${apiBase}/kpi/${siteId}/summary`,
        energyMix: (siteId: string) => `${apiBase}/kpi/${siteId}/energy-mix`,
        peakDemand: (siteId: string) => `${apiBase}/kpi/${siteId}/peak-demand`,
      },

      assets: {
        pv: (siteId: string) => `${apiBase}/assets/pv/${siteId}`,
        pvSummary: (siteId: string) => `${apiBase}/assets/pv/${siteId}/summary`,
        pvHistory: (siteId: string) => `${apiBase}/assets/pv/${siteId}/history`,
        battery: (siteId: string) => `${apiBase}/assets/battery/${siteId}`,
        batterySummary: (siteId: string) => `${apiBase}/assets/battery/${siteId}/summary`,
        batteryHistory: (siteId: string) => `${apiBase}/assets/battery/${siteId}/history`,
        generator: (siteId: string) => `${apiBase}/assets/generator/${siteId}`,
        generatorSummary: (siteId: string) => `${apiBase}/assets/generator/${siteId}/summary`,
        generatorHistory: (siteId: string) => `${apiBase}/assets/generator/${siteId}/history`,
        grid: (siteId: string) => `${apiBase}/assets/grid/${siteId}`,
        gridSummary: (siteId: string) => `${apiBase}/assets/grid/${siteId}/summary`,
        gridHistory: (siteId: string) => `${apiBase}/assets/grid/${siteId}/history`,
      },

      weather: {
        current: (siteId: string) => `${apiBase}/weather/${siteId}`,
        solarPosition: (siteId: string) => `${apiBase}/weather/${siteId}/solar-position`,
        forecast: (siteId: string) => `${apiBase}/weather/${siteId}/forecast`,
      },

      topology: {
        get: (siteId: string) => `${apiBase}/topology/${siteId}`,
        update: (siteId: string) => `${apiBase}/topology/${siteId}`,
        live: (siteId: string) => `${apiBase}/topology/${siteId}/live`,
      },

      sld: {
        runtime: (siteId: string) => `${apiBase}/sld/sites/${siteId}/runtime`,
        runtimeLive: (siteId: string) => `${apiBase}/sld/sites/${siteId}/runtime/live`,
      },

      eventLogs: {
        list: `${apiBase}/event-logs`,
        create: `${apiBase}/event-logs`,
        export: `${apiBase}/event-logs/export`,
        soe: (siteId: string) => `${apiBase}/event-logs/soe/${siteId}`,
      },

      ems: {
        config: (siteId: string) => `${apiBase}/ems/${siteId}`,
        status: (siteId: string) => `${apiBase}/ems/${siteId}/status`,
        setMode: (siteId: string) => `${apiBase}/ems/${siteId}/mode`,
        setParameters: (siteId: string) => `${apiBase}/ems/${siteId}/parameters`,
        history: (siteId: string) => `${apiBase}/ems/${siteId}/history`,
      },

      controlAuthority: {
        get: (deviceId: string) => `${apiBase}/control-authority/${deviceId}`,
        set: (deviceId: string) => `${apiBase}/control-authority/${deviceId}`,
        listBySite: (siteId: string) => `${apiBase}/control-authority/site/${siteId}`,
        summaryBySite: (siteId: string) => `${apiBase}/control-authority/site/${siteId}/summary`,
      },

      diagnostics: {
        get: (siteId: string) => `${apiBase}/diagnostics/${siteId}`,
      },

      security: {
        events: `${apiBase}/security/events`,
        summary: `${apiBase}/security/summary`,
      },

      audit: {
        list: `${apiBase}/audit`,
        export: `${apiBase}/audit/export`,
      },

      registerStandard: {
        json: `${this.baseUrl}/register-standard`,
        html: `${this.baseUrl}/register-standard/html`,
      },

      document: {
        get: (siteId: string) => `${apiBase}/sld/sites/${siteId}/documents`,
        delete: (id: string) => `${apiBase}/sld/documents/${id}`,
        upload: `${apiBase}/sld/sites/{siteId}/svg-upload`,
      },

      carbon: {
        summary: (siteId: string) => `${apiBase}/carbon/summary/${siteId}`,
      },
    };
  }

  getEndpoints(): APIEndpoints {
    return this.endpoints;
  }
}
